import {merge, Observable}                                from 'rxjs';
import {filter, map, pairwise, share, throttleTime, withLatestFrom} from 'rxjs/operators';
import {TestScheduler}                                    from 'rxjs/testing';

import MediaEventType            from '../../../../Solutions/Shared/Constants/MediaEventType';
import SyntheticMediaEventType   from '../../../../Solutions/Shared/Constants/SyntheticMediaEventType';
import IMediaEventsStreamPayload from '../../../../Solutions/Shared/Interfaces/IMediaEventsStreamPayload';
import UiEventType               from '../../../../Ui/Shared/Constants/UiEventType';
import IUiEventsStreamPayload    from '../../../../Ui/Shared/Interfaces/IUiEventsStreamPayload';
import Playback                  from '../../Playback/Playback';
import Frame                     from '../Frame';

const THROTTLE_DELAY_DURATION = 200;
const BLOCK_MOUSE_MOVE_AFTER_CLICK_DURATION = 500;

/**
 * Filters UI events to include only those which should activate the UI.
 *
 * Additionally, adds logic to ensure that a `mousemove` event on the
 * `<Frame>` component will only show the UI if it occurs at least 500ms
 * after a click event.
 */

const filterUiEvents = (
    uiEvents$: Observable<IUiEventsStreamPayload>,
    frameUpdate$: Observable<Frame>,
    scheduler?: TestScheduler
): Observable<IUiEventsStreamPayload> => {
    const timeSource = scheduler ? scheduler : Date;

    let lastFrameTapTime: number = -Infinity;

    return uiEvents$
        .pipe(
            withLatestFrom(frameUpdate$),
            filter(([uiEvent, {areControlsIdle}]) => {
                let isEventBlocked = false;

                if (uiEvent.type === UiEventType.FRAME_TAP) lastFrameTapTime = timeSource.now();

                if (
                    (
                        uiEvent.type === UiEventType.FRAME_MOUSE_MOVE &&
                        (timeSource.now() - lastFrameTapTime) < BLOCK_MOUSE_MOVE_AFTER_CLICK_DURATION
                    ) || (
                    uiEvent.type === UiEventType.FRAME_TAP && !areControlsIdle
                    )
                ) {
                    isEventBlocked = true;
                }

                return uiEvent.type !== UiEventType.FRAME_MOUSE_LEAVE && !isEventBlocked;
            }),
            map(([uiEvent]) => uiEvent)
        );
};

/**
 * Filters media events to include only those which should activate the UI.
 */

const filterMediaEvents = (
    mediaEvents$: Observable<IMediaEventsStreamPayload>
): Observable<IMediaEventsStreamPayload> =>
    mediaEvents$
        .pipe(
            filter(
                ({type}) => ([
                    MediaEventType.ENDED,
                    MediaEventType.PAUSE,
                    MediaEventType.VOLUME_CHANGE
                ] as Array<MediaEventType | SyntheticMediaEventType>)
                    .includes(type)
            )
        );

/**
 * Filters playback updates to emit when the status changes from loading to playing
 */

const filterPlaybackUpdate = (
    playbackUpdate$: Observable<Playback>
) =>
    playbackUpdate$.pipe(
        pairwise(),
        filter(([{isLoading}, {isPlaying}]) => isLoading && isPlaying)
    );

/**
 * Creates a single stream from all possible events which
 * should activate the UI.
 *
 * The stream is multicasted to ensure that it is only
 * created once regardless of subscriber count.
 *
 * The stream is throttled to prevent thrashing from mousemove events.
 */

const createActiveStream = (
    uiEvents$: Observable<IUiEventsStreamPayload>,
    mediaEvents$: Observable<IMediaEventsStreamPayload>,
    frameUpdate$: Observable<Frame>,
    playbackUpdate$: Observable<Playback>,
    scheduler?: TestScheduler
): Observable<void> =>
    merge(
        filterPlaybackUpdate(playbackUpdate$),
        filterUiEvents(uiEvents$, frameUpdate$, scheduler),
        filterMediaEvents(mediaEvents$)
    )
        .pipe(
            throttleTime(THROTTLE_DELAY_DURATION, scheduler),
            map(() => void 0),
            share()
        );

export {
    createActiveStream as default,
    filterMediaEvents,
    filterUiEvents
};